import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { getCreateTemplate, getInwardDetail } from "../api/returnInwardApi";
import type { ReturnSourceType } from "../config/returnKinds";
import type { InwardRoute } from "../routing/resolveInwardRoute";
import type { ReturnInwardAction } from "../state/returnInwardReducer";
import type { ReturnSerialRow } from "../types";

/**
 * Tải dữ liệu phiếu một lần khi mở trang:
 * - Tạo mới từ phiếu cần nhập (có sourceCode): lấy mẫu tạo và mã phiếu NK.
 * - Sửa / xem (có id): lấy chi tiết phiếu và các serial đã lưu.
 * Lỗi của backend (HTTP 500) do lớp gọi API hiện thông báo; ở đây chỉ đánh dấu không tải được.
 */
export const useReturnInwardData = (
  route: InwardRoute,
  sourceType: ReturnSourceType,
  dispatch: React.Dispatch<ReturnInwardAction>,
) => {
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const requestRef = React.useRef(0);

  const { action, sourceCode, id, branchCode } = route;

  React.useEffect(() => {
    const requestId = ++requestRef.current;
    const isCurrent = () => requestId === requestRef.current;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        if (id && (action === "EDIT" || action === "DETAIL")) {
          const res = await getInwardDetail(action === "EDIT" ? "edit" : "detail", id);
          if (!isCurrent()) return;
          if (!res?.success || !res.data) {
            setLoadError(true);
            return;
          }
          const detail = res.data;
          dispatch({
            type: "LOAD_DETAIL",
            header: detail.tblInventoryTransactionModel,
            order: detail.tblInventoryTransactionModel,
            orderLines: detail.inventoryTransactionRequestSourceModels ?? [],
            rows: (detail.inventoryTransactionSerialModels ?? []).map(
              (row): ReturnSerialRow => ({ ...row, primaryQuantity: row.primaryQuantity || 0, type: "scanned" }),
            ),
          });
          return;
        }

        if (sourceCode && action === "CREATE") {
          const res = await getCreateTemplate(sourceCode, sourceType, branchCode);
          if (!isCurrent()) return;
          if (!res?.success || !res.data) {
            setLoadError(true);
            return;
          }
          const model = res.data.tblInventoryTransactionModel ?? {};
          dispatch({
            type: "LOAD_TEMPLATE",
            order: model,
            orderLines: res.data.inventoryTransactionRequestSourceModels ?? [],
            headerPatch: {
              transactionCode: model.transactionCode ?? "",
              sourceDescription: model.sourceDescription ?? null,
              // NHAP_DOI_TRA: backend không trả kho, người dùng chọn. Loại khác: kho theo chứng từ
              subInvId: model.toInvId ?? null,
              toInvId: model.toInvId ?? null,
              toInvCode: model.toInvCode ?? null,
              toInvName: model.toInvName ?? null,
            },
          });
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("[ReturnInward] tải dữ liệu lỗi", error);
        setLoadError(true);
        NotificationExtension.Fails("Không tải được dữ liệu phiếu nhập.");
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    load();
  }, [action, sourceCode, id, branchCode, sourceType, dispatch]);

  return { loading, loadError };
};
